import { Component, Prop, h, State, Watch, Element } from '@stencil/core';
import { htmlSanitizer } from '../../utils/html-sanitizer';

interface HeroData {
  title?: string;
  description?: string;
  headlineTag?: string;
  mediaType?: 'image' | 'video-loop' | 'video-single';
  desktopImageImgPath?: string;
  desktopImageAltText?: string;
  videofile?: string;
  backgroundImage?: string;
  poster?: string;
  mediaAlt?: string;
  height?: '100%' | '75%';
  theme?: 'light' | 'dark';
  mode?: 'light' | 'dark';
  showOverlay?: boolean;
  showTint?: boolean;
  overlayOpacity?: number;
  showBounceArrow?: boolean;
  showArrow?: boolean;
  anchorId?: string;
  isTopOfPage?: boolean;
  cta?: Array<{
    title: string;
    buttonColor: string;
    link: string;
    ctaVideoUrl: string;
    ctaVideoTitle?: string;
    target: string;
    style: string;
    type: string;
    anchorLabelRequired?: string;
    conversionType?: string;
    ctaVideoAltText?: string;
    ctaVideoImagePath?: string;
    noFollow?: string;
    url?: string;
  }>;
  primaryButtonText?: string;
  primaryButtonUrl?: string;
  primaryButtonTarget?: string;
  secondaryButtonText?: string;
  secondaryButtonUrl?: string;
  secondaryButtonTarget?: string;
  tertiaryButtonText?: string;
  tertiaryButtonUrl?: string;
  tertiaryButtonTarget?: string;
}
@Component({
  tag: 'visa-hero',
  styleUrl: 'visa-hero.scss',
  shadow: false,
})
export class VisaHero {
  @Element() el: HTMLElement;

  // JSON data prop for AEM integration
  @Prop() data: string = '';

  // Mode prop for authoring
  @Prop() mode: 'authoring' | 'publish' = 'publish';

  // Fallback individual props for backward compatibility
  @Prop() headlineTag: string;
  @Prop() title: string = '';
  @Prop() description: string = '';
  @Prop() primaryButtonText: string = '';
  @Prop() secondaryButtonText: string = '';
  @Prop() primaryButtonUrl: string = '';
  @Prop() secondaryButtonUrl: string = '';
  @Prop() backgroundImage: string = '';
  @Prop() height: '100%' | '75%' = '100%';
  @Prop() showTint: boolean = true;
  @Prop() showArrow: boolean = true;
  @Prop() theme: 'light' | 'dark' = 'light';

  @State() heroData: HeroData = {};
  @State() isTopOfPage: boolean = false;
  @State() videoLoaded: boolean = false;
  @State() videoError: boolean = false;
  @State() isInView: boolean = false;
  @State() isMuted: boolean = true;

  private videoElement: HTMLVideoElement;
  private intersectionObserver: IntersectionObserver;

  @Watch('data')
  parseData() {
    if (this.data && this.data.trim() !== '') {
      try {
        this.heroData = JSON.parse(this.data);
        // this.getCtaButtons();
      } catch (error) {
        console.error('Error parsing hero data JSON:', error);
        this.heroData = {};
      }
    } else {
      this.heroData = {};
    }
  }

  componentWillLoad() {
    this.parseData();
  }

  componentDidLoad() {
    // this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  private onVideoLoaded = () => {
    this.videoLoaded = true;
    // Start playback for looped videos once loaded
    const mediaType = this.getMediaType();
    if (mediaType === 'video-loop') {
      this.videoElement.play().catch(error => {
        console.warn('Video autoplay failed:', error);
      });
    }
  };

  private onVideoEnded = () => {
    const mediaType = this.getMediaType();
    if (mediaType === 'video-single') {
      this.videoElement.pause();
      this.videoElement.currentTime = 0; // Reset to beginning
    }
  };

  private onVideoError = (event: Event) => {
    console.error('Video loading error:', event);
    // Fallback to poster image or background image
    this.videoLoaded = false;
    this.videoError = true;
  };

  private handleVideoClick = () => {
    if (!this.videoElement) return;

    const mediaType = this.getMediaType();
    if (mediaType === 'video-single') {
      if (this.videoElement.paused) {
        this.videoElement.play().catch(console.error);
      } else {
        this.videoElement.pause();
      }
    }
  };

  private getTitle(): string {
    return this.heroData.title || this.title;
  }

  private getDescription(): string {
    // FIX: fallback to prop if not set in heroData
    return this.heroData.description || this.description || '';
  }

  private getBackgroundImage(): string {
    return (
      this.heroData.backgroundImage ||
      this.heroData.desktopImageImgPath ||
      this.backgroundImage ||
      ''
    );
  }

  private getMediaType(): string {
    return this.heroData.mediaType || 'image';
  }

  private getMediaUrl(): string {
    return this.heroData.videofile || this.heroData.backgroundImage || this.backgroundImage || '';
  }

  private getPosterImage(): string {
    return this.heroData.desktopImageImgPath || this.getBackgroundImage();
  }

  private getHeight(): '100%' | '75%' {
    return this.heroData.height || this.height;
  }

  private getTheme(): 'light' | 'dark' {
    return this.heroData.mode || this.heroData.theme || this.theme;
  }

  private getShowArrow(): boolean {
    return this.heroData.showArrow !== undefined
      ? this.heroData.showArrow
      : this.heroData.showBounceArrow !== undefined
        ? this.heroData.showBounceArrow
        : this.showArrow;
  }

  private hasDescription(): boolean {
    return !!(this.heroData.description || this.description);
  }

  private getMediaAlt(): string {
    return this.heroData.mediaAlt || this.heroData.desktopImageAltText || '';
  }

  get effectiveShowTint() {
    if (this.heroData && typeof this.heroData.showTint === 'boolean') {
      return this.heroData.showTint;
    }
    return this.showTint;
  }

  get effectiveTheme() {
    if (this.heroData && typeof this.heroData.theme === 'string') {
      return this.heroData.theme as 'light' | 'dark';
    }
    return this.theme;
  }

  render() {
    const currentHeight = this.getHeight();
    const currentTheme = this.getTheme();
    const showTint = this.effectiveShowTint;

    const showBounceArrow =
      this.getShowArrow() &&
      currentHeight === '100%' &&
      (this.heroData.isTopOfPage || this.isTopOfPage);

    const mediaType = this.getMediaType();
    const isVideo = mediaType === 'video-loop' || mediaType === 'video-single';

    const Tag = this.headlineTag || this.heroData.headlineTag || 'h1';

    // In authoring mode, show a simplified version
    if (this.mode === 'authoring') {
      return (
        <div
          class={`vs-hero-container authoring-mode vs-hero-container-theme-${currentTheme}`}
          style={{ backgroundImage: `url(${this.getPosterImage()})` }}
        >
          <div class="vs-hero-authoring-overlay">
            {showTint && <div class="vs-hero-tint"></div>}

            <div class="vs-hero-content-wrapper">
              <div class="vs-hero-copy-container">
                <Tag class="vs-hero-title" innerHTML={htmlSanitizer(this.getTitle())}></Tag>
                {this.heroData.cta && this.heroData.cta.length > 0 && (
                  <div class="vs-hero-button-group">
                    {this.heroData.cta.map((btn, index) => {
                      const isVideo = btn.type === 'video';
                      const isButton = btn.style === 'button';
                      // const isLink = btn.style === 'link' || btn.style === 'tertiary';

                      const state = btn.url || (isVideo && btn.ctaVideoUrl) ? 'active' : 'inactive';

                      const themeOverride = currentTheme === 'light' ? 'dark' : 'light';

                      // Choose component type based on style
                      const ComponentTag = isButton ? 'vsr-button-v2' : 'vsr-link-button-v2';

                      return (
                        <ComponentTag
                          key={index}
                          href={btn.url || '#'}
                          variant={btn.buttonColor || 'primary'}
                          themeOverride={themeOverride}
                          state={state}
                          target={btn.target || '_self'}
                          aria-label={btn.title}
                          ctaLinkType={btn.type}
                          ctaVideoUrl={btn.ctaVideoUrl}
                          ctaVideoTitle={btn.ctaVideoTitle}
                        >
                          <span innerHTML={htmlSanitizer(btn.title)}></span>
                        </ComponentTag>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            {this.hasDescription() && (
              <div class="vs-hero-description-bottom">
                <p class="vs-hero-description" innerHTML={htmlSanitizer(this.getDescription())}></p>
              </div>
            )}
            {showBounceArrow && (
              <button class="vs-hero-moving-arrow" aria-label="arrow paddle">
                <svg
                  aria-hidden="true"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4 L12 16 L18 10 L12 16 L6 10"
                    stroke="currentColor"
                    stroke-width="1"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      );
    }
    const containerProps = !isVideo
      ? {
          role: 'img',
          'aria-label': this.getMediaAlt() || 'Hero background image',
        }
      : {};
    const hasPoster = !isVideo || !this.videoLoaded;
    return (
      <div
        class={`vs-hero-container ${hasPoster || isVideo ? 'has-bg-image-video' : ''} ${this.heroData.isTopOfPage || this.isTopOfPage ? 'is-top-of-page' : ''} ${currentHeight === '75%' ? 'medium-height' : ''} vs-hero-container-theme-${currentTheme} ${isVideo ? 'video-background' : ''} ${this.videoLoaded ? 'video-loaded' : ''}`}
        style={hasPoster ? { '--hero-bg': `url('${this.getPosterImage()}')` } : {}}
        {...containerProps}
        data-animation-container
        // onClick={isVideo && this.isMuted ? this.handleVideoPlayClick : undefined}
      >
        {isVideo && (
          <video
            ref={el => (this.videoElement = el)}
            class="vs-hero-video"
            poster={this.getPosterImage()}
            muted={this.isMuted}
            autoPlay
            playsInline
            loop={mediaType === 'video-loop'}
            preload="metadata"
            onClick={this.handleVideoClick}
            onLoadedData={this.onVideoLoaded}
            onEnded={this.onVideoEnded}
            onError={this.onVideoError}
            aria-label={this.getMediaAlt() || 'Hero video'}
          >
            <source src={this.getMediaUrl()} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}

        {showTint && <div class="vs-hero-tint"></div>}

        <div class="vs-hero-content-wrapper">
          <div class="vs-hero-copy-container">
            <div class="vs-hero-title-wrapper">
              <Tag
                class="vs-hero-title headerReveal1"
                innerHTML={htmlSanitizer(this.getTitle())}
              ></Tag>
            </div>
            {this.heroData.cta && this.heroData.cta.length > 0 && (
              <div class="vs-hero-button-group">
                {this.heroData.cta.map((btn, index) => {
                  const isVideo = btn.type === 'video';
                  const isButton = btn.style === 'button';
                  const target =
                    btn.target == 'new' ? '_blank' : btn.target == 'modal' ? 'modal' : '_self';
                  // const isLink = btn.style === 'link' || btn.style === 'tertiary';
                  const state =
                    btn.url || btn.link || (isVideo && btn.ctaVideoUrl) ? 'active' : 'inactive';

                  const themeOverride = currentTheme === 'light' ? 'dark' : 'light';

                  // Choose component type based on style
                  const ComponentTag = isButton ? 'vsr-button-v2' : 'vsr-link-button-v2';

                  return (
                    <ComponentTag
                      key={index}
                      href={btn.url || btn.link || '#'}
                      variant={btn.buttonColor || 'primary'}
                      themeOverride={themeOverride}
                      state={state}
                      target={target}
                      aria-label={btn.title}
                      ctaLinkType={btn.type}
                      ctaVideoUrl={btn.ctaVideoUrl}
                      ctaVideoTitle={btn.ctaVideoTitle}
                    >
                      <span innerHTML={htmlSanitizer(btn.title)}></span>
                    </ComponentTag>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {this.hasDescription() && (
          <div class="vs-hero-description-bottom">
            <p
              class="vs-hero-description subHeaderReveal"
              innerHTML={htmlSanitizer(this.getDescription())}
            ></p>
          </div>
        )}
        {showBounceArrow && (
          <button class="vs-hero-moving-arrow" aria-label="arrow paddle">
            <svg
              aria-hidden="true"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4 L12 16 L18 10 L12 16 L6 10"
                stroke="currentColor"
                stroke-width="1"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
    );
  }
}
